import { FC, useState } from 'react';
import { Pencil, Trash2, Check, X } from 'lucide-react';
import {
  useUpdateUserNoteMutation,
  useDeleteUserNoteMutation,
  UserNote,
} from '@teachur/http-client';
import { Button } from '@new/common/ui/Button/Button';
import { Textarea } from '@new/common/ui/Textarea/Textarea';

interface NoteItemProps {
  note: UserNote;
}

export const NoteItem: FC<NoteItemProps> = ({ note }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(note.note);

  const { mutate: updateNote, isPending: isUpdating } = useUpdateUserNoteMutation();
  const { mutate: deleteNote, isPending: isDeleting } = useDeleteUserNoteMutation();

  const onEditSubmit = () => {
    if (!editValue.trim()) return;
    updateNote(
      { id: note.id, data: { note: editValue } },
      { onSuccess: () => setIsEditing(false) }
    );
  };

  const onEditCancel = () => {
    setEditValue(note.note);
    setIsEditing(false);
  };

  const onDelete = () => {
    deleteNote(note.id);
  };

  const formattedDate = new Date(note.createdAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  if (isEditing) {
    return (
      <div className="flex flex-col gap-2 py-3 border-b border-(--neutral-black-NB100)">
        <Textarea value={editValue} onChange={setEditValue} rows={3} />
        <div className="flex items-center gap-2 justify-end">
          <Button
            variant="primary"
            size="sm"
            onClick={onEditSubmit}
            disabled={isUpdating}
            leftIcon={<Check size={14} />}
            text="Save"
          />
          <Button
            variant="inverted"
            size="sm"
            onClick={onEditCancel}
            disabled={isUpdating}
            leftIcon={<X size={14} />}
            text="Cancel"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1 py-3 border-t border-(--neutral-black-NB100)">
      <div className="flex items-center justify-between">
        <span className="[font-size:var(--fs-l4)] text-(--neutral-black-NB500) [font-family:var(--font-montserrat)]">
          {formattedDate}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="inverted"
            size="icon-sm"
            onClick={() => setIsEditing(true)}
            aria-label="Edit note"
            leftIcon={<Pencil size={14} />}
          />
          <Button
            variant="inverted"
            size="icon-sm"
            onClick={onDelete}
            disabled={isDeleting}
            aria-label="Delete note"
            className="text-(--danger-D700) hover:text-(--danger-D700) hover:bg-(--danger-D100)"
            leftIcon={<Trash2 size={14} />}
          />
        </div>
      </div>
      <p className="[font-family:var(--font-montserrat)] [font-size:var(--fs-l3)] text-(--neutral-black-NB900) m-0! h-24 overflow-y-auto overflow-x-hidden break-words">
        {note.note}
      </p>
    </div>
  );
};
